import { useTheme } from '@emotion/react'
import { TheWestSection } from '@news-mono/common'
import {
    BreachEvent,
    DataLayerEventName,
    useToggleState,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import { TheWestRegionalSections } from '@west-australian-newspapers/publication-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    StyledBreachWallContent2022,
    StyledRegistrationInfo,
    StyledPackagesContainer,
    BreachWallWrapper,
    StyledFooterAndTermsContainer,
} from './BreachWallContent.styled'
import {
    BreachWallContentType,
    BreachWallStyle,
    mapSubscriptionPackagesToFlatList,
    SubscriptionPackageButtonStyle,
    SubscriptionPackageOptions,
    SubscriptionPackageTheme,
    SubscriptionPackageType,
    SubscriptionPriceObject,
} from '../../helpers/BreachWallTypes'
import { useSection } from '../../../../__helpers'
import { getAttribution } from '../../../../errors/ErrorPage'
import { toItemListId, DEFAULT_CART_QUANTITY } from '../../../../tracking'
import { SubscriptionPackages } from '../../common'
import SubscriptionPackage from '../SubscriptionPackage/SubscriptionPackage'
import { AflSubscriptionPackage } from '../AflSubscriptionPackage/AflSubscriptionPackage'
import { SubscriptionPackageRedesign2026 } from '../SubscriptionPackageRedesign2026/SubscriptionPackageRedesign2026'

const BLACK_FRIDAY_MOBILE_IMAGE = require('../../assets/thewest-digital-hero-variation-black-friday-cropped.jpg')
const BLACK_FRIDAY_DESKTOP_IMAGE = require('../../assets/thewest-digital-hero-variation-black-friday.jpg')
const FOOTY_LIVES_HERE_MOBILE_IMAGE = require('../../assets/thewest-digital-hero-variation-footy-lives-here-mobile.jpg')
const FOOTY_LIVES_HERE_DESKTOP_IMAGE = require('../../assets/thewest-digital-hero-variation-footy-lives-here.jpg')

// Window Variables
declare global {
    interface Window {
        getPackagePrices?: (
            packageHeading: string,
        ) => SubscriptionPriceObject[] | undefined
        setPackagePrices?: (
            packageHeading: string,
            order: SubscriptionPriceObject[],
            selected?: number,
        ) => boolean
    }
}

export interface BreachWallContentProps {
    title: () => JSX.Element | null
    terms: () => JSX.Element | null
    footer: () => JSX.Element | null
    mediaBanner: () => JSX.Element | null
    breachBanner: () => JSX.Element | null
    subscriptionPackages?: SubscriptionPackages
    // Values passed down from the BreachWallContentWrapper in relation to the breach
    breachCreativeName: string
    breachType: BreachWallContentType
    breachDeal: string
    breachStyle: BreachWallStyle
    onEvent: (event: BreachEvent) => void
    // Values passed down if it's a test BreachWallContent
    isTest: boolean | undefined
    isRegionalOverride: boolean
}

export interface SubscriptionPackageProps {
    breachCreativeName: string
    breachDeal: string
    breachStyle: BreachWallStyle
    breachContent?: BreachWallContentType
    packageInfo: SubscriptionPackageOptions
    packagePrices?: SubscriptionPriceObject[]
    selectedRadioPackage?: number
    masthead?: string
    packageTheme?: SubscriptionPackageTheme
    cancellationMessage?: string
    googleSKU?: string
    onEvent: (event: BreachEvent) => void
    isRegionalOverride: boolean
    isSwgPage?: boolean
    desiredRedirect?: string
}

export interface PackageOptions {
    theme: SubscriptionPackageTheme
    type?: SubscriptionPackageType
    gridColumnPosition?: SubscriptionPackageOptions['columnPosition']
    isRecommended?: boolean
}
export interface BreachOptions {
    style: BreachWallStyle
    useBreachDealStyle?: boolean
    isRegional?: boolean
}
export interface PackageThemeProps {
    packageOptions: PackageOptions
    breachOptions: BreachOptions
    buttonStyle?: SubscriptionPackageButtonStyle
}

type StoredProvidedData = {
    selected?: number
    order?: SubscriptionPriceObject[]
}

export const BreachWallContent: React.FC<BreachWallContentProps> = ({
    mediaBanner: renderMediaBanner,
    breachBanner: renderBanner,
    terms: renderTerms,
    title: renderTitleSection,
    footer: renderFooter,
    subscriptionPackages,
    breachCreativeName,
    breachType,
    breachDeal,
    breachStyle,
    onEvent,
    isRegionalOverride,
}) => {
    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'
    const section = useSection<TheWestSection>()
    const [version, setVersion] = useState(0)

    const providedSubscriptionPackageOptions = useMemo(
        () => new Map<string, StoredProvidedData>(),
        [],
    )

    const isRegional = !isRegionalOverride && section in TheWestRegionalSections
    const showBreachBanner = canShowBreachBanner(breachStyle) && !isRegional

    // Assign background image if utilising new breach design.
    const packageImages = useMemo(() => {
        let largeImage = undefined
        let smallImage = undefined

        switch (breachType) {
            case 'black-friday':
                largeImage = BLACK_FRIDAY_DESKTOP_IMAGE
                smallImage = BLACK_FRIDAY_MOBILE_IMAGE
                break
            case 'afl-footy-lives-here-breach-screen':
                largeImage = FOOTY_LIVES_HERE_DESKTOP_IMAGE
                smallImage = FOOTY_LIVES_HERE_MOBILE_IMAGE
                break
        }

        return {
            largeImage,
            smallImage,
        }
    }, [breachType])

    // when everything is loaded, fire off a view_item_list GA4 event
    useEffect(() => {
        const availableDeal = breachDeal === '' ? 'default' : breachDeal
        const breachAvailablePayload: BreachEvent['payload'] = {
            paywallType: 'freemium',
            leadDepth: 0,
            creative: breachCreativeName,
            deal: availableDeal,
            breachScreenType: 'Premium - Inline',
            popupMethod: 'auto',
            pricepoint: [7, 8],
        }
        onEvent({
            type: DataLayerEventName.breachScreenAvailable,
            originator: 'breachscreen',
            payload: breachAvailablePayload,
        })

        const flatSubscriptions = subscriptionPackages
            ? mapSubscriptionPackagesToFlatList(subscriptionPackages)
            : undefined
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            flatSubscriptions?.map((pkg, index) => ({
                index,
                deal_name: breachType,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                item_list_id: toItemListId(breachCreativeName),
                price: pkg.weeklyPrice,
                promo_price: pkg.promoPrice || 0,
                discount: pkg.discount || 0,
                coupon: pkg.offerCode || '',
                quantity: pkg.quantity || DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            })) || []
        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isFullWidthBelowDesktop = ['eofy', 'footy-lives-here'].includes(
        breachStyle,
    )
    const TitleComponent = useCallback(
        () => renderTitleSection(),
        [renderTitleSection],
    )()

    const getExistingPackageWithHeading = useCallback(
        (heading: string) =>
            subscriptionPackages?.find((pkg) => pkg.heading === heading),
        [subscriptionPackages],
    )

    const getPackagePrices = useCallback(
        (packageHeading: string) => {
            const packageObj = getExistingPackageWithHeading(packageHeading)
            if (!packageObj) return

            return packageObj.type === 'single-subscription'
                ? [packageObj]
                : packageObj.prices
        },
        [getExistingPackageWithHeading],
    )
    const setPackagePrices = useCallback(
        (
            packageHeading: string,
            order: SubscriptionPriceObject[],
            selected?: number,
        ) => {
            const packageObj = getExistingPackageWithHeading(packageHeading)
            if (!packageObj || packageObj.type !== 'radio-list-subscriptions')
                return false

            providedSubscriptionPackageOptions.set(packageHeading, {
                selected,
                order,
            })
            // Fire off the react version update, which fires a re-render
            setVersion(version + 1)
            return true
        },
        [
            getExistingPackageWithHeading,
            providedSubscriptionPackageOptions,
            version,
        ],
    )

    // Do a typeof check to allow it to pass over on VR tests and such
    if (typeof window !== 'undefined') {
        window.getPackagePrices = getPackagePrices
        window.setPackagePrices = setPackagePrices
    }

    // New 2024 Optimizely Breachwall design
    return (
        <BreachWallWrapper
            isFullWidthBelowDesktop={isFullWidthBelowDesktop}
            breachType={breachType}
        >
            <StyledBreachWallContent2022
                backgroundColor={'#F4F5F5'}
                largeImage={packageImages.largeImage}
                smallImage={packageImages.smallImage}
                breachStyle={breachStyle}
            >
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                {renderMediaBanner()}
                {breachStyle !== 'default' ? (
                    <StyledRegistrationInfo breachStyle={breachStyle}>
                        {breachStyle && TitleComponent}
                    </StyledRegistrationInfo>
                ) : (
                    TitleComponent
                )}
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                <StyledPackagesContainer
                    breachType={breachType}
                    numberOfColumns={subscriptionPackages?.length}
                    useGridLayout={canUseGridLayout(breachType)}
                >
                    {subscriptionPackages &&
                        subscriptionPackages.map(
                            (subscriptionPackageItem, index) => {
                                const heading = subscriptionPackageItem.heading
                                const providedData =
                                    providedSubscriptionPackageOptions.get(
                                        heading,
                                    )
                                const theme =
                                    subscriptionPackageItem.packageTheme

                                // TODO: Remove the original subscription package design
                                const SubscriptionPackageComponent =
                                    theme === 'afl-footy-design-2026'
                                        ? AflSubscriptionPackage
                                        : theme === 'redesign-2026'
                                        ? SubscriptionPackageRedesign2026
                                        : SubscriptionPackage

                                return (
                                    <SubscriptionPackageComponent
                                        key={index}
                                        breachCreativeName={breachCreativeName}
                                        breachDeal={breachDeal}
                                        breachStyle={breachStyle}
                                        breachContent={
                                            showBreachBanner
                                                ? breachType
                                                : undefined
                                        }
                                        onEvent={onEvent}
                                        packageInfo={subscriptionPackageItem}
                                        masthead={masthead}
                                        isRegionalOverride={isRegionalOverride}
                                        packagePrices={providedData?.order}
                                        selectedRadioPackage={
                                            providedData?.selected
                                        }
                                    />
                                )
                            },
                        )}
                </StyledPackagesContainer>
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                <StyledFooterAndTermsContainer breachType={breachType}>
                    {renderFooter()}
                    {renderTerms()}
                    {renderBanner()}
                </StyledFooterAndTermsContainer>
            </StyledBreachWallContent2022>
        </BreachWallWrapper>
    )
}

function useBackgroundImageVariation(breachStyle: BreachWallStyle) {
    const backgroundVariation =
        useToggleState()?.['tp-subscription-hero-variation'] || 'a'

    // check for the background variation against the breachStyle
    switch (breachStyle) {
        case 'vanishing-cousins':
            return 'vanishing-cousins'
        case 'black-friday':
            return 'black-friday'
        case 'eofy':
            return 'eofy'
        case 'footy-lives-here':
            return 'footy-lives-here'
        case 'summer-of-cricket':
            return 'summer-of-cricket'
    }

    return backgroundVariation
}

function canShowBreachBanner(breachStyle: BreachWallStyle) {
    if (
        breachStyle === 'black-friday' ||
        breachStyle === 'eofy' ||
        breachStyle === 'footy-lives-here' ||
        (breachStyle !== 'default' && breachStyle !== 'summer-of-cricket')
    ) {
        return true
    }
    return false
}

function canUseGridLayout(breachType: BreachWallContentType) {
    const gridLayoutTypes: BreachWallContentType[] = [
        'afl-footy-lives-here-breach-screen',
    ]
    return gridLayoutTypes.includes(breachType)
}

BreachWallContent.displayName = 'BreachWallContent'
export default BreachWallContent
